import { useMemo, useState } from "react";
import { useData } from "@/lib/store";
import { STATUS_LABELS, TYPE_META, formatDue, daysUntil, cn } from "@/lib/utils";
import { useSearchParams } from "react-router-dom";

const COLUMNS = ["todo", "doing", "review", "done"];

export default function BoardPage() {
  const { items, updateItem, spaces } = useData();
  const [params] = useSearchParams();
  const spaceFilter = params.get("space");
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const filtered = useMemo(
    () => (spaceFilter ? items.filter((i) => i.space_id === spaceFilter) : items),
    [items, spaceFilter]
  );

  const byCol = useMemo(() => {
    const g = { todo: [], doing: [], review: [], done: [] };
    filtered.forEach((i) => { if (g[i.status]) g[i.status].push(i); });
    return g;
  }, [filtered]);

  const onDrop = (status) => {
    if (!dragId) return;
    const it = items.find((i) => i.id === dragId);
    if (it && it.status !== status) updateItem(dragId, { status });
    setDragId(null); setOverCol(null);
  };

  return (
    <div className="animate-fade-in-up">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="board-columns">
        {COLUMNS.map((col) => (
          <div
            key={col}
            onDragOver={(e) => { e.preventDefault(); setOverCol(col); }}
            onDragLeave={() => setOverCol((c) => (c === col ? null : c))}
            onDrop={() => onDrop(col)}
            className={cn(
              "rounded-hero bg-surface-container p-4 min-h-[400px] transition-colors duration-200 ease-md",
              overCol === col && "bg-primary-container/60"
            )}
            data-testid={`board-col-${col}`}
          >
            <div className="flex items-center justify-between mb-4 px-1">
              <span className="text-sm font-medium text-surface-on">{STATUS_LABELS[col]}</span>
              <span className="text-xs text-surface-onVariant bg-surface-containerHigh rounded-full px-2 py-0.5">{byCol[col].length}</span>
            </div>
            <div className="space-y-3">
              {byCol[col].map((it) => (
                <BoardCard key={it.id} item={it} spaces={spaces} onDragStart={() => setDragId(it.id)} />
              ))}
              {byCol[col].length === 0 && (
                <div className="text-xs text-surface-onVariant text-center py-8">Empty</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BoardCard({ item, spaces, onDragStart }) {
  const space = spaces.find((s) => s.id === item.space_id);
  const type = TYPE_META[item.type];
  const dd = daysUntil(item.due_date);
  const overdue = dd !== null && dd < 0 && item.status !== "done";
  return (
    <div
      draggable
      onDragStart={onDragStart}
      data-testid={`board-card-${item.id}`}
      className="bg-surface rounded-[16px] p-3 border border-outline-variant hover:border-primary/40 hover:shadow-sm cursor-grab active:cursor-grabbing transition-[border-color,box-shadow] duration-200 ease-md"
    >
      <div className={cn("text-sm font-medium text-surface-on leading-snug", item.status === "done" && "line-through opacity-70")}>
        {item.title}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <span className={cn("text-[10px] font-medium tracking-wider uppercase px-2 py-0.5 rounded-full", type.color)}>{type.label}</span>
        {space && <span className="text-[10px] text-surface-onVariant inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: space.color }} />{space.name}</span>}
        {item.due_date && <span className={cn("text-[11px]", overdue ? "text-danger font-medium" : "text-surface-onVariant")}>{formatDue(item.due_date)}</span>}
      </div>
    </div>
  );
}
